import React, { useRef, useState } from "react";
import {
  SafeAreaView,
  View,
  Text,
  Image,
  Dimensions,
  StyleSheet,
  TextInput,
  ScrollView,
  Pressable,
  FlatList,
  TouchableWithoutFeedback,
  KeyboardAvoidingView,
  Keyboard,
  findNodeHandle,
} from "react-native";
import type { View as ViewType } from "react-native";
import { AntDesign } from "@expo/vector-icons";
import { getManufacturerInfoForCharger } from "../services/charger-service";
import { notificationService } from "../services/notification-service";
import { isSuccessful } from "../services/api-requests";
import type { RootStackParamList } from "../navigation/stackTypes";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ChargerManufacturerInfo } from "../models/charger-model";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Platform } from "react-native";
import LoadingButton from "../components/LoadingButton";
import Header from "../components/Header";
// @ts-ignore - Platform-specific file (.native/.web)
import SerialCameraAddon from "../components/ocr/SerialCameraAddon";

const { width, height } = Dimensions.get("window");

type ChargerValidationNavigationProps = NativeStackScreenProps<
  RootStackParamList,
  "ChargerValidation"
>;

const ChargerValidation: React.FC<ChargerValidationNavigationProps> = ({
  navigation,
  route,
}) => {
  const [manufacturer, setManufacturer] = useState<string>("Emporia");
  const [serial, setSerial] = useState("");
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [dropdownTop, setDropdownTop] = useState(0);
  const [dropdownLeft, setDropdownLeft] = useState(0);
  const [serialFocused, setSerialFocused] = useState(false);

  const dropdownButtonRef = useRef<ViewType>(null);

  const handleManufacturerSelect = (value: string) => {
    setManufacturer(value);
    setIsDropdownVisible(false); // Close the dropdown after selection
  };

  //validate charger before moving to the next step
  const validateSerial = async () => {
    if (serial === "") return;
    const infoResponse = await getManufacturerInfoForCharger(
      serial,
      manufacturer
    );

    if (
      !infoResponse ||
      !isSuccessful(infoResponse.status) ||
      !infoResponse.payload
    ) {
      notificationService.sendNotification(
        "error",
        infoResponse.payload?.message ?? "Error registering your charger"
      );
    } else {
      // Explicitly assert that payload is an array of ChargerManufacturerInfo
      const manufacturerArray: ChargerManufacturerInfo = infoResponse.payload;

      if (!manufacturerArray) {
        notificationService.sendNotification(
          "error",
          "No manufacturer info found"
        );
      }

      let manufacturer;
      if (Array.isArray(manufacturerArray)) {
        manufacturer = manufacturerArray[0];
      } else {
        manufacturer = manufacturerArray;
      }

      const storedUserSession = await AsyncStorage.getItem("userSession");

      let userEmail = null;
      if (storedUserSession) {
        const userSession = JSON.parse(storedUserSession);
        userEmail = userSession.email;
      }

      navigation.navigate("RegisterUpdateCharger", {
        unit_serial: serial,
        capacity: manufacturer.charge_rate_amps,
        name: manufacturer.name,
        manufacturer: manufacturer.manufacturer,
        chargerEmail: userEmail ?? "",
        returnToOnboarding: route.params?.returnToOnboarding ?? false,
      });
    }
  };

  const closeDropdown = () => {
    if (isDropdownVisible) setIsDropdownVisible(false);
    Keyboard.dismiss();
  };

  const manufacturers = ["Emporia", "Virtual"];

  const toggleDropdown = () => {
    const anchor = findNodeHandle(dropdownButtonRef.current);

    if (
      typeof dropdownButtonRef.current?.measure === "function" &&
      anchor != null
    ) {
      dropdownButtonRef.current.measure((x, y, width, height, pageX, pageY) => {
        setDropdownTop(pageY + height);
        setDropdownLeft(pageX - width);
        setIsDropdownVisible((prev) => !prev);
      });
    } else {
      setIsDropdownVisible((prev) => !prev);
    }
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#0B1E1D" }}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        style={{ flex: 1 }}
      >
        <TouchableWithoutFeedback
          onPress={(e: React.TouchEvent | any) => {
            const target = e.nativeEvent.target;
            if (
              target?.tagName !== "INPUT" &&
              target?.getAttribute?.("role") !== "textbox"
            ) {
              closeDropdown();
            }
          }}
          accessible={false}
          disabled={!isDropdownVisible}
        >
          <View style={{ flex: 1 }}>
            <View style={[styles.containerBgColor, { flex: 1 }]}>
              {Platform.OS !== "web" && (
                <Header headerText="Register Charger" />
              )}

              <ScrollView
                contentContainerStyle={{ paddingBottom: 20 }}
                keyboardShouldPersistTaps="always"
                onScrollBeginDrag={() => {}}
                onScrollEndDrag={() => {}}
              >
                <Image
                  source={require("../../assets/ChargerStationBg.png")}
                  style={
                    Platform.OS === "web"
                      ? {
                          aspectRatio: 1,
                        }
                      : {
                          width: "100%",
                          height: 250,
                          marginBottom: 16,
                        }
                  }
                  alt="Background Image"
                />
                <View className="p-6">
                  <Text className="text-lg font-semibold text-white">
                    My Chargers
                  </Text>
                  <Text className="text-sm font-medium text-white">
                    Register your first charger by entering the charger's serial
                    number below:
                  </Text>

                  <View style={styles.inputRow}>
                    <View style={styles.imageContainer}>
                      <Image
                        source={require("../../assets/stationPlugIn.png")}
                        resizeMode="cover"
                        style={styles.image}
                      />
                    </View>

                    <View style={styles.inputColumn}>
                      <Text className="text-base font-semibold text-white">
                        Select Manufacturer
                      </Text>
                      <View
                        style={styles.dropdownContainer}
                        ref={dropdownButtonRef}
                      >
                        <Pressable onPress={toggleDropdown}>
                          <View style={styles.dropdownRow}>
                            <TextInput
                              value={manufacturer}
                              style={styles.dropdown}
                              editable={false}
                              placeholder="Select Manufacturer"
                              placeholderTextColor="#fff"
                              className={
                                Platform.OS === "web"
                                  ? "p-3 text-white rounded-full"
                                  : "p-1 text-white rounded-full"
                              }
                            />
                            <AntDesign
                              name="down"
                              size={16}
                              color="white"
                              style={styles.dropdownIcon}
                            />
                          </View>
                        </Pressable>
                      </View>

                      <Text className="text-base font-semibold text-white">
                        Serial Number
                      </Text>
                      <View style={styles.serialInputContainer}>
                        <TextInput
                          placeholder={serialFocused ? "" : "Enter serial number"}
                          placeholderTextColor={"#fff"}
                          style={[
                            styles.serialInput,
                            serialFocused && {
                              borderWidth: 2,
                              borderColor: "#39BEB7",
                            },
                          ]}
                          className={
                            Platform.OS === "web"
                              ? "p-3 text-white rounded-full"
                              : "mt-1 p-1 text-white rounded-full"
                          }
                          value={serial}
                          onFocus={() => {
                            setSerialFocused(true);
                            //if (serial === "") setSerial("");
                          }}
                          onBlur={() => setSerialFocused(false)}
                          onChange={(e) => setSerial(e.nativeEvent.text)}
                          clearTextOnFocus={true}
                        />
                        <View style={styles.cameraIcon}>
                          <SerialCameraAddon
                            manufacturer_name={manufacturer}
                            onDetected={(detectedSerial: string) => setSerial(detectedSerial)}
                            singleMode={true}
                            iconColor="#fff"
                          />
                        </View>
                      </View>
                    </View>
                  </View>

                  {/* <Pressable
                  style={{ backgroundColor: serial === "" ? "#636363" : "#39BEB7", zIndex: 1 }}
                  className="p-3 mt-4 rounded-full"
                  onPress={() => validateSerial()}
                > */}
                  <View>
                    <LoadingButton
                      onPress={validateSerial}
                      // className={`p-3 mt-4 rounded-full`}
                      backgroundColor={`${
                        serial === "" ? "#636363" : "#39BEB7"
                      }`}
                      style={{
                        paddingVertical: 0,
                        paddingHorizontal: 0,
                        marginTop: 20,
                        borderRadius: 20,
                      }}
                    >
                      <Text className="text-center text-white">
                        Continue to Registration
                      </Text>
                    </LoadingButton>
                  </View>
                  {/* </Pressable> */}

                  <View>
                    <View style={styles.line} />
                    <View className="flex flex-row gap-2 items-center">
                      <AntDesign
                        name="questioncircle"
                        size={17}
                        color="white"
                      />
                      <Text className="text-sm font-bold text-white">
                        Where can I find my charger's serial number?
                      </Text>
                    </View>
                    <Text className="mt-2 text-sm font-medium text-white">
                      Your charger's serial number is located on one of the
                      sides or on the front of your charger.
                    </Text>
                  </View>
                </View>
              </ScrollView>

              {isDropdownVisible && (
                <View
                  style={{
                    position: "absolute",
                    width: "50%",
                    left: dropdownLeft + (Platform.OS === "web" ? 0 : 230),
                    top:
                      Platform.OS === "web"
                        ? dropdownTop + 4
                        : dropdownTop - (Platform.OS === "ios" ? 50 : 0),
                    backgroundColor: "#292D33",
                    borderRadius: 8,
                    zIndex: 9999,
                    padding: 10,
                    shadowColor: "#000",
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.25,
                    shadowRadius: 4,
                    elevation: 5,
                  }}
                >
                  <FlatList
                    data={manufacturers}
                    ItemSeparatorComponent={() => (
                      <View style={{ height: 1, backgroundColor: "#ccc" }} />
                    )}
                    renderItem={({ item }) => {
                      const isSelected = item === manufacturer;

                      return (
                        <Pressable
                          onPress={() => handleManufacturerSelect(item)}
                        >
                          <View
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "space-between",
                              paddingVertical: 10,
                              paddingHorizontal: 15,
                            }}
                          >
                            <Text
                              style={{
                                fontWeight: isSelected ? "bold" : "normal",
                                color: "#fff",
                              }}
                            >
                              {item}
                            </Text>
                            {isSelected && (
                              <AntDesign
                                name="check"
                                size={16}
                                color="white"
                                style={{
                                  backgroundColor: "#39BEB7",
                                  borderRadius: 999,
                                  padding: 2,
                                }}
                              />
                            )}
                          </View>
                        </Pressable>
                      );
                    }}
                    keyExtractor={(item) => item}
                  />
                </View>
              )}
            </View>
          </View>
        </TouchableWithoutFeedback>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  containerBgColor: {
    backgroundColor: "#0B1E1D",
  },
  line: Platform.select({
    web: {
      borderBottomColor: "#636363",
      borderBottomWidth: 1,
      borderStyle: "dashed",
      marginVertical: 20,
    },
    default: {
      borderBottomColor: "#636363",
      borderBottomWidth: 1,
      marginVertical: 20,
    },
  }),
  inputRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 20, // Space between the image and input column
  },
  imageContainer: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1, // Make sure image is centered
  },
  image: {
    height: 54,
    width: 42,
  },
  inputColumn: {
    flex: 2, // Give more space to the input section
    justifyContent: "center",
  },
  dropdownContainer: {
    backgroundColor: "#292D33",
    borderRadius: 50,
    marginBottom: 10,
    marginTop: 5,
  },
  dropdownRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dropdown: {
    padding: 15,
    color: "#fff",
    backgroundColor: "#292D33",
    borderRadius: 50,
    textAlign: "center",
    fontSize: 14,
    fontWeight: "bold",
    flex: 1,
    height: 30, // Ensure height matches the serial number input
  },
  serialInput: {
    backgroundColor: "#292D33",
    padding: 15,
    paddingLeft: 20,
    color: "#fff",
    borderRadius: 50,
    textAlign: "left",
    fontSize: 14,
    height: 30, // Ensure it matches the height of the dropdown input
    flex: 1,
  },
  serialInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#292D33",
    borderRadius: 50,
  },
  cameraIcon: {
    position: "absolute",
    right: 15,
    justifyContent: "center",
    alignItems: "center",
  },
  dropdownIcon: {
    marginRight: 10,
  },
  dropdownOptionsContainer: {
    backgroundColor: "#292D33",
    borderRadius: 8,
    marginTop: 5,
    marginHorizontal: 150,
    position: "absolute",
    top: 150,
    width: "50%",
    zIndex: 10,
  },
  dropdownOption: {
    padding: 15,
    color: "#fff",
    fontSize: 16,
    borderBottomColor: "#636363",
    borderBottomWidth: 1,
  },
});
export default ChargerValidation;
